import { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { useApp } from 'common/AppContext';
import { FaGears } from 'react-icons/fa6';

const SETTING_LABELS = {
    bedrock_model_orchestrator: {
        helpText: 'The multi-turn agent that drives the browser. Vision-quality matters here. Sonnet is the default; cheaper models can save real money but may be less precise on complex UIs.',
        label: 'Orchestrator model'
    },
    bedrock_model_planner: {
        helpText: 'Single-turn structured-output calls (test-plan generation, work-item plans, TestRail-case plans). Cheaper models work well here.',
        label: 'Planner model'
    }
};

const SOURCE_VARIANTS = {
    db: 'primary',
    default: 'secondary',
    env: 'info'
};

const formatPrice = n => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

export const AdminSettings = () => {

    const { api } = useApp();

    const [data, setData] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingKey, setSavingKey] = useState(null);
    const [savedKey, setSavedKey] = useState(null);

    const load = useCallback(() => {

        setLoading(true);
        setError(null);

        return api.get('/api/admin/settings')
            .then(d => {

                setData(d);

                // Hydrate drafts from current dbValue (or empty if not overridden)
                setDrafts(Object.fromEntries((d?.settings || []).map(s => [s.key, s.dbValue || ''])));

            })
            .catch(e => setError(e?.err || e?.message || String(e)))
            .finally(() => setLoading(false));

    }, [api]);

    useEffect(() => {

        load();

    }, [load]);

    const save = async key => {

        setSavingKey(key);
        setError(null);
        setSavedKey(null);

        try {

            await api.request('PUT', `/api/admin/settings/${key}`, { value: drafts[key] || '' });
            await load();
            setSavedKey(key);
            setTimeout(() => setSavedKey(prev => (prev === key ? null : prev)), 1500);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setSavingKey(null);

        }

    };

    const modelOptions = useMemo(() => {

        const list = (data?.models || []).map(m => ({ id: m.id, pricing: m.pricing }));

        // Sort cheapest first by input rate.
        return list.sort((a, b) => (a.pricing?.input || 0) - (b.pricing?.input || 0));

    }, [data]);

    if (loading)
        return (
            <Container className="py-4">
                <Spinner animation="border" />
            </Container>
        );

    return (

        <Container className="py-4">

            <div className="d-flex align-items-center gap-2 mb-1">
                <FaGears className="text-primary" size={22} />
                <h3 className="m-0">
                    Settings
                </h3>
            </div>
            <p className="text-muted">
                Runtime knobs. DB values override the corresponding env vars; clearing a field falls back to
                <code className="mx-1">
                    BEDROCK_MODEL_ORCHESTRATOR
                </code>
                / etc., then the hardcoded default. Changes take effect on the next call (no restart needed).
            </p>

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            <div className="d-flex flex-column gap-3">

                {(data?.settings || []).map(setting => {

                    const meta = SETTING_LABELS[setting.key] || { helpText: '', label: setting.key };
                    const isDirty = (drafts[setting.key] || '') !== (setting.dbValue || '');

                    return (

                        <Card className="border-0 shadow-sm" key={setting.key}>
                            <Card.Body>

                                <div className="d-flex align-items-start justify-content-between mb-2">
                                    <div>
                                        <h5 className="m-0">
                                            {meta.label}
                                        </h5>
                                        <code className="text-muted small">
                                            {setting.key}
                                        </code>
                                    </div>
                                    <div className="text-end">
                                        <Badge bg={SOURCE_VARIANTS[setting.source] || 'secondary'}>
                                            source:
                                            {' '}
                                            {setting.source}
                                        </Badge>
                                        <div className="text-muted small mt-1">
                                            currently:
                                            {' '}
                                            <code>
                                                {setting.effective || '(not set)'}
                                            </code>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-muted small">
                                    {meta.helpText}
                                </p>

                                <Form.Group className="mb-2">
                                    <Form.Label className="small">
                                        Override value
                                        {' '}
                                        <span className="text-muted">
                                            (leave blank to fall back to env / default)
                                        </span>
                                    </Form.Label>
                                    <Form.Select
                                        onChange={e => setDrafts(d => ({ ...d, [setting.key]: e.target.value }))}
                                        value={drafts[setting.key] || ''}
                                    >
                                        <option value="">
                                            (no override — use
                                            {' '}
                                            {setting.envValue || 'default'}
                                            )
                                        </option>
                                        {modelOptions.map(m => (
                                            <option key={m.id} value={m.id}>
                                                {m.id}
                                                {' '}
                                                — in
                                                {formatPrice(m.pricing.input)}
                                                /M, out
                                                {formatPrice(m.pricing.output)}
                                                /M
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        disabled={!isDirty || savingKey === setting.key}
                                        onClick={() => save(setting.key)}
                                        size="sm"
                                        variant="primary"
                                    >
                                        {savingKey === setting.key ? 'Saving…' : 'Save'}
                                    </Button>
                                    {savedKey === setting.key && (
                                        <span className="text-success small">
                                            ✓ Saved
                                        </span>
                                    )}
                                    {setting.updated_by_email && (
                                        <span className="text-muted small ms-auto">
                                            Last set by
                                            {' '}
                                            {setting.updated_by_email}
                                            {' '}
                                            on
                                            {' '}
                                            {new Date(setting.updated_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>

                            </Card.Body>
                        </Card>

                    );

                })}

            </div>

            <Card className="border-0 shadow-sm mt-3">
                <Card.Body>
                    <Card.Title className="h6">
                        Model pricing reference (USD per 1M tokens)
                    </Card.Title>
                    <Table responsive className="mb-0 small" size="sm">
                        <thead>
                            <tr>
                                <th>
                                    Model
                                </th>
                                <th className="text-end">
                                    Input
                                </th>
                                <th className="text-end">
                                    Output
                                </th>
                                <th className="text-end">
                                    Cache write
                                </th>
                                <th className="text-end">
                                    Cache read
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {modelOptions.map(m => (
                                <tr key={m.id}>
                                    <td>
                                        <code>
                                            {m.id}
                                        </code>
                                    </td>
                                    <td className="text-end">
                                        {formatPrice(m.pricing.input)}
                                    </td>
                                    <td className="text-end">
                                        {formatPrice(m.pricing.output)}
                                    </td>
                                    <td className="text-end">
                                        {formatPrice(m.pricing.cacheWrite)}
                                    </td>
                                    <td className="text-end">
                                        {formatPrice(m.pricing.cacheRead)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

        </Container>

    );

};
